<script setup lang="ts">
import { useDisplay } from "vuetify";

// Props
const props = defineProps({
  // The name of the laboratory
  laboratoryName: {
    type: String,
    required: true,
  },

  // Indicates if the laboratory name is loading
  isLaboratoryNameLoading: {
    type: Boolean,
    required: true,
  },

  // The URL of the laboratory's logo
  logoUrl: {
    type: String,
    required: true,
  },
});

// Handle navigation
const router = useRouter();

// Localization logic
const { t, locale } = useI18n();
const switchLocalePath = useSwitchLocalePath();
const selectedLanguage = ref<string>(locale.value);

// Check the size of the user's screen
const { smAndDown } = useDisplay();

// Locations
const locations = [
  t("landing_page.header.locations.sofia"),
  t("landing_page.header.locations.paris"),
  t("landing_page.header.locations.new_york"),
  t("landing_page.header.locations.plovdiv"),
  t("landing_page.header.locations.london"),
];

/**
 * Switches the app locale and updates the path to the corresponding language version.
 * @param lang - The language code to switch to ("en" or "bg").
 */
const changeLanguage = (lang: "en" | "bg"): void => {
  // Update selected language
  selectedLanguage.value = lang;

  // Update the locale using i18n
  locale.value = lang;

  // Update the cookie with the selected language
  useCookie("i18n_redirected", { path: "/" }).value = lang;

  const newPath = switchLocalePath(lang);
  if (newPath) {
    selectedLanguage.value = lang;
    router.push(newPath);
  }
};
</script>

<template>
  <!-- Logo and Company Info -->
  <v-row class="pa-2 pt-0">
    <v-col cols="10" sm="11">
      <!-- Company Logo and Name -->
      <v-row class="d-flex align-center my-0 mb-2">
        <!-- Logo -->
        <v-img
          :src="logoUrl"
          alt="Laboratory Logo"
          style="max-width: 40px; min-width: 40px; height: auto"
          class="mx-2"
          loading="lazy"
        ></v-img>

        <!-- Name -->
        <div class="text-h5 font-weight-bold">
          {{ isLaboratoryNameLoading ? "Loading..." : laboratoryName }}
        </div>
      </v-row>

      <!-- Location and Contact Information -->
      <v-col>
        <!-- Locations Row -->
        <v-row class="text-caption d-flex align-center ga-1">
          <!-- Icon -->
          <v-icon
            icon="mdi-city-variant-outline"
            size="small"
            class="mr-1"
          ></v-icon>

          <!-- Location & Separator -->
          <span v-for="(location, index) in locations" :key="index">
            <span>{{ location }}</span>
            <span v-if="index < locations.length - 1"> | </span>
          </span>
        </v-row>

        <!-- Contact Info -->
        <v-row class="text-caption d-flex align-center ga-1">
          <!-- Phone Link -->
          <client-only>
            <v-tooltip location="bottom" :open-delay="700" :close-delay="300">
              <template v-slot:activator="{ props }">
                <NuxtLink
                  to="tel:+359561235456"
                  v-bind="props"
                  class="text-black d-flex align-center text-decoration-none"
                  aria-label="Call +359 56 123-54-56"
                >
                  <v-icon
                    icon="mdi-phone-outline"
                    size="small"
                    class="mr-1"
                  ></v-icon>
                  <span
                    class="text-decoration-underline"
                    style="text-underline-offset: 2px"
                  >
                    +359 56 123-54-56
                  </span>
                </NuxtLink>
              </template>

              <!-- Tooltip text -->
              <span>{{ t("tooltips.landing.give_us_a_call") }}</span>
            </v-tooltip>
          </client-only>

          <!-- Spacer -->
          <span class="mx-1"></span>

          <!-- Email Link -->
          <client-only>
            <v-tooltip location="bottom" :open-delay="700" :close-delay="300">
              <template v-slot:activator="{ props }">
                <NuxtLink
                  to="mailto:service@green-lab.com"
                  v-bind="props"
                  class="text-black d-flex align-center text-decoration-none"
                  aria-label="Email service@green-lab.com"
                >
                  <v-icon
                    icon="mdi-email-outline"
                    size="small"
                    class="mr-1"
                  ></v-icon>
                  <span
                    class="text-decoration-underline"
                    style="text-underline-offset: 2px"
                  >
                    service@green-lab.com
                  </span>
                </NuxtLink>
              </template>

              <!-- Tooltip text -->
              <span>{{ t("tooltips.landing.send_us_an_email") }}</span>
            </v-tooltip>
          </client-only>
        </v-row>
      </v-col>
    </v-col>

    <v-col cols="2" sm="1">
      <!-- Company Logo and Name -->
      <v-row class="d-flex align-center justify-end my-0 mb-2">
        <v-menu
          activator="parent"
          close-on-content-click
          transition="scale-transition"
        >
          <template #activator="{ props }">
            <v-btn
              v-if="!smAndDown"
              v-bind="props"
              variant="text"
              color=""
              :text="t('tooltips.navbar.language')"
              class="text-body-2"
              rounded="lg"
              prepend-icon="mdi-web"
              append-icon="mdi-chevron-down"
              aria-label="Select language"
              style="min-width: fit-content"
            >
            </v-btn>

            <v-btn
              v-if="smAndDown"
              v-bind="props"
              variant="text"
              color=""
              icon="mdi-web"
              aria-label="Select language"
              style="min-width: fit-content"
            >
            </v-btn>
          </template>

          <v-list>
            <!-- English -->
            <v-list-item
              value="en"
              @click="changeLanguage('en')"
              aria-label="Select English"
              :class="{ 'bg-grey-lighten-4': selectedLanguage === 'en' }"
            >
              <v-list-item-title class="d-flex align-center">
                <CircleFlags
                  country="us"
                  class="mr-2"
                  style="width: 20px; height: 20px"
                />
                <span
                  :class="{
                    'font-weight-bold': selectedLanguage === 'en',
                  }"
                  class="text-body-2"
                >
                  English
                </span>
              </v-list-item-title>
            </v-list-item>

            <!-- Bulgarian -->
            <v-list-item
              value="bg"
              @click="changeLanguage('bg')"
              aria-label="Select Bulgarian"
              :class="{ 'bg-grey-lighten-4': selectedLanguage === 'bg' }"
            >
              <v-list-item-title class="d-flex align-center">
                <CircleFlags
                  country="bg"
                  class="mr-2"
                  style="width: 20px; height: 20px"
                />
                <span
                  :class="{
                    'font-weight-bold': selectedLanguage === 'bg',
                  }"
                  class="text-body-2"
                >
                  Bulgarian
                </span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-row>
    </v-col>
  </v-row>
</template>
